import React, {useRef, useState} from 'react';
import {Overlay} from 'react-bootstrap';
import {useSelector} from 'react-redux';

import {Glyphicon} from '../../Common/GlyphIcon';
import {EditColumnPopover} from './Table/Popovers/EditColumnPopover';
import {NAME_PLACEHOLDER} from './Constants';
import {useInsertColumns, useColumnEditor} from '../../../Hooks/QueryToolColumnHooks';
import {useSortColumns} from '../../../Hooks/QueryToolTableHooks';

export const Header = (props) => {
    const {column: columnFromProps, dragHandleProps} = props;
    const {guid} = columnFromProps.columnDef.data;
    
    const {insertColumnId} = useSelector(state => state.queryToolState);
    const insertColumns = useInsertColumns();
    const sortColumns = useSortColumns();
    const {
        column,
        isFirstColumn,
        isLastColumn,
        isPlaceholder,
        isNewColumn,
        hasFilters,
        isEditing,
        sortDirection,
        indicator,
        openEditor,
        saveColumn,
        popoverController,
    } = useColumnEditor(guid);

    const editTarget = useRef(null);
    const [showInsertIcon, setShowInsertIcon] = useState(false);
    const [showFirstIcon, setShowFirstIcon] = useState(false);

    const newColumnClass = isNewColumn ? 'sb-query-new-column' : '';
    const activeClass = isEditing || isPlaceholder ? 'sb-query-column-active' : '';
    const hasFiltersClass = hasFilters ? 'has-filters' : '';

    const sortIcon = !sortDirection ? 'sort' : (sortDirection === 'desc' ? 'sort-alpha-up' : 'sort-alpha-down');

    const handleInsertBefore = () => {
        insertColumns(null, [{name: NAME_PLACEHOLDER}]);
    };

    const handleInsertAfter = () => {
        insertColumns(guid, [{name: NAME_PLACEHOLDER}]);
    };

    const renderIndicator = () => {
        switch (indicator) {
            case 'group':
                return <img src="/img/group.svg" width={12} height={12} alt="Identity" />;
            case 'filter':
                return <img src="/img/filter.svg" width={12} height={12} alt="Default Filtered" />;
            default:
                return <Glyphicon glyph="pencil" />;
        }
    };

    return (
        <div key={guid}>
            <InsertBeforeButton
                visible={isFirstColumn}
                onClick={handleInsertBefore}
                onHover={setShowFirstIcon}
                showIcon={showFirstIcon}
            />
            <div
                className={`sb-query-column-source ${newColumnClass} ${activeClass} ${hasFiltersClass}`}
                title={column?.name}
                {...dragHandleProps}
            >
                {column?.name}
            </div>
            <InsertAfterButton
                isLastColumn={isLastColumn}
                insertColumnId={insertColumnId}
                currentGuid={guid}
                showInsertIcon={showInsertIcon}
                setShowInsertIcon={setShowInsertIcon}
                onInsertAfter={handleInsertAfter}
            />
            <ColumnTitle
                ref={editTarget}
                title={column?.title}
                indicator={renderIndicator()}
                sortIcon={sortIcon}
                sortColumns={sortColumns}
                guid={guid}
                onEditToggle={openEditor}
                classes={`${newColumnClass} ${activeClass} ${hasFiltersClass}`}
            />
            <Overlay
                container={editTarget}
                target={editTarget.current}
                onHide={saveColumn}
                show={isEditing}
                rootClose={true}
            >
                <EditColumnPopover controller={{...popoverController, columnGuid: guid}} />
            </Overlay>
        </div>
    );
};

const InsertBeforeButton = ({visible, onClick, onHover, showIcon}) => {
    if (!visible) {
        return null;
    }

    return (
        <div
            className="sb-query-first-insert-area"
            onMouseOver={() => onHover(true)}
            onMouseLeave={() => onHover(false)}
        >
            {showIcon && (
                <Glyphicon
                    glyph="plus-sign"
                    className="sb-query-insert-icon"
                    onClick={onClick}
                />
            )}
        </div>
    );
};

const InsertAfterButton = ({isLastColumn, insertColumnId, currentGuid, showInsertIcon, setShowInsertIcon, onInsertAfter}) => {
    if (isLastColumn) {
        return (
            <div className="sb-query-last-insert-icon">
                <Glyphicon
                    glyph="plus-sign"
                    className="sb-query-insert-icon"
                    onClick={onInsertAfter}
                />
            </div>
        );
    }

    if (insertColumnId === currentGuid || showInsertIcon) {
        return (
            <Glyphicon
                glyph="plus-sign"
                className="sb-query-insert-icon"
                onMouseOver={() => setShowInsertIcon(true)}
                onMouseLeave={() => setShowInsertIcon(false)}
                onClick={onInsertAfter}
            />
        );
    }

    return null;
};

const ColumnTitle = React.forwardRef(({title, indicator, sortIcon, sortColumns, guid, onEditToggle, classes}, ref) => {
    return (
        <div className={`sb-query-column-name ${classes}`} ref={ref}>
            <span className="sb-query-column-name-text" title={title} onClick={onEditToggle}>
                {indicator}
                {title}
            </span>
            <span onClick={(event) => sortColumns(event, guid)}>
                <Glyphicon glyph={sortIcon} className="sb-query-sort-icon" />
            </span>
        </div>
    );
});
